import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type { CredentialingStatus, PublicCredentialingStatus } from '../../types';
import { IntakePill } from '../shell/IntakePill';

import {
  CREDENTIALING_PAGE_SIZE,
  CREDENTIALING_STATUS_LABELS,
  credentialingStatusTone,
} from './credentialingStatus';

/** The design's table header: 11px/700 tracked uppercase on a tinted row. */
const headerCellSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: '#6b6b78',
  backgroundColor: '#fafafd',
} as const;

export function CredentialingStatusSelect({
  value,
  onChange,
  disabled,
  label = 'Status',
}: {
  value: CredentialingStatus;
  onChange: (value: CredentialingStatus) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <TextField
      select
      size="small"
      label="Status"
      slotProps={{
        select: { SelectDisplayProps: { 'aria-label': label, 'aria-labelledby': undefined } },
      }}
      value={value}
      disabled={disabled}
      onChange={event => {
        const status = event.target.value;
        if (status in CREDENTIALING_STATUS_LABELS) onChange(status as CredentialingStatus);
      }}
      sx={{ minWidth: 150 }}
    >
      {Object.entries(CREDENTIALING_STATUS_LABELS).map(([status, text]) => (
        <MenuItem key={status} value={status}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Only public fields are rendered, even when the staff response contains metadata. */
export function CredentialingStatusPanel({
  rows,
  pending,
  refreshing = false,
  failed,
  offset,
  onPage,
  onRetry,
  providerLabel,
  payerLabel,
  onUpdate,
  rowExtra,
  saving = false,
  header,
  body,
  children,
}: {
  rows: PublicCredentialingStatus[];
  /** Nothing to show yet: the table gives way to a progress bar. */
  pending: boolean;
  /**
   * The rows on screen are being refetched (after a save, say). They stay
   * mounted under a thin overlaid indicator, so the page keeps its height and
   * does not jump; the controls are disabled until the fresh rows land.
   */
  refreshing?: boolean;
  failed: boolean;
  offset: number;
  onPage: (offset: number) => void;
  onRetry: () => void;
  providerLabel?: (id: string) => ReactNode;
  payerLabel?: (id: string) => ReactNode;
  onUpdate?: (row: PublicCredentialingStatus, status: CredentialingStatus) => void;
  /** Staff-only detail under a row's status, e.g. why a payer rejected it. */
  rowExtra?: (row: PublicCredentialingStatus) => ReactNode;
  saving?: boolean;
  /**
   * Replaces the default title and blurb. The clinician view passes the
   * practice-level summary card; the staff view passes nothing and keeps the
   * heading it has always had.
   */
  header?: ReactNode;
  /**
   * Replaces the record-only table with a caller-supplied body — the
   * roster-driven matrix. When it is absent the panel renders exactly the
   * table it always has, which is also the fallback whenever a practice has no
   * payer roster on file.
   */
  body?: ReactNode;
  children?: ReactNode;
}) {
  const busy = pending || refreshing || saving;
  return (
    <Stack spacing={2}>
      {header ?? (
        <>
          <Typography variant="h6" component="h2">
            Credentialing
          </Typography>
          <Typography color="text.secondary">
            Status for each provider and payer, updated by JotPsych. These records do not indicate
            completion for the whole practice.
          </Typography>
        </>
      )}
      {children}
      {pending ? (
        <LinearProgress aria-label="Loading credentialing statuses" />
      ) : failed ? (
        <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>
          We couldn’t load credentialing statuses.
        </Alert>
      ) : body ? (
        <Box sx={{ position: 'relative' }}>
          {refreshing && <RefreshIndicator />}
          {body}
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          {offset === 0
            ? 'No credentialing statuses have been added yet.'
            : 'No more credentialing statuses.'}
        </Alert>
      ) : (
        <TableContainer
          sx={{
            position: 'relative',
            border: '1px solid #e2e2ea',
            borderRadius: '10px',
            '& .MuiTableCell-root': { borderColor: '#eeeef4' },
          }}
        >
          {refreshing && <RefreshIndicator />}
          <Table aria-label="Credentialing statuses">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Provider</TableCell>
                <TableCell sx={headerCellSx}>Payer</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    {providerLabel?.(row.user_id) ?? row.user_id}
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    {payerLabel?.(row.payer_uuid) ?? row.payer_uuid}
                  </TableCell>
                  <TableCell>
                    {onUpdate ? (
                      <CredentialingStatusSelect
                        label={`Status for ${row.user_id} / ${row.payer_uuid}`}
                        value={row.status}
                        disabled={saving || refreshing}
                        onChange={status => onUpdate(row, status)}
                      />
                    ) : (
                      <IntakePill
                        label={CREDENTIALING_STATUS_LABELS[row.status]}
                        tone={credentialingStatusTone(row.status)}
                      />
                    )}
                    {rowExtra?.(row)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          disabled={offset === 0 || busy}
          onClick={() => onPage(Math.max(0, offset - CREDENTIALING_PAGE_SIZE))}
        >
          Previous
        </Button>
        <Typography variant="body2">Page {offset / CREDENTIALING_PAGE_SIZE + 1}</Typography>
        <Button
          disabled={busy || failed || rows.length < CREDENTIALING_PAGE_SIZE}
          onClick={() => onPage(offset + CREDENTIALING_PAGE_SIZE)}
        >
          Next
        </Button>
        <Button disabled={busy} onClick={onRetry}>
          Refresh
        </Button>
      </Stack>
    </Stack>
  );
}

/** Sits on the table's top edge so it takes no height of its own. */
function RefreshIndicator() {
  return (
    <LinearProgress
      aria-label="Refreshing credentialing statuses"
      sx={{ position: 'absolute', top: 0, left: 0, right: 0, borderRadius: '10px 10px 0 0' }}
    />
  );
}
